import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../platform/apiClient';
import type { Questionnaire } from '../types';

// Cache key: scoped per slug so different questionnaires are cached
// independently. Switching in the AppHeader picker just changes the slug
// argument; React Query handles the rest.
const questionnaireKey = (slug: string) => ['questionnaire', slug] as const;

interface QuestionnaireEnvelope {
  versionId: string;
  versionNumber: number;
  schema: Questionnaire;
}

const fetchQuestionnaire = async (slug: string): Promise<Questionnaire> => {
  const envelope = await apiClient.get<QuestionnaireEnvelope>(
    `/questionnaires/${slug}`,
  );
  return envelope.schema;
};

const saveQuestionnaire = async (
  questionnaire: Questionnaire,
): Promise<Questionnaire> => {
  const envelope = await apiClient.put<QuestionnaireEnvelope>(
    `/questionnaires/${questionnaire.id}`,
    questionnaire,
  );
  return envelope.schema;
};

// slug comes from the API's questionnaire list (via the URL param or
// AppHeader's picker) — there is no hardcoded fallback ID. Query stays
// disabled until a real slug is known.
export const useQuestionnaire = (slug?: string) =>
  useQuery({
    queryKey: questionnaireKey(slug ?? ''),
    queryFn: () => fetchQuestionnaire(slug as string),
    enabled: !!slug,
  });

export const useSaveQuestionnaire = (slug: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveQuestionnaire,
    onSuccess: (data) => {
      queryClient.setQueryData(questionnaireKey(slug), data);
    },
  });
};
